import { Component, inject, input, signal } from '@angular/core';
import { NonNullableFormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DokaModal, DokaModalController, DokaTextField, DokaButton } from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import {
  CatalogClient,
  CreateCatalogCategoryModel,
  ICreateCatalogCategoryModel,
} from 'src/api/services/generated/api-clients';
import { CreateCatalogCategoryFormType } from './create-catalog-category.form.type';
import { firstValueFrom } from 'rxjs';
import { DokaI18NextService } from '@doka/i18next';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from 'src/app/shared/services/toast.service';

@Component({
  templateUrl: 'create-catalog-category-slide-over.component.html',
  imports: [DokaModal, I18NextEagerPipe, ReactiveFormsModule, DokaTextField, DokaButton],
})
export class CreateCatalogCategorySlideOverComponent {
  private readonly catalogClient = inject(CatalogClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly fb = inject(NonNullableFormBuilder);
  protected readonly i18n = inject(DokaI18NextService);
  private readonly i18next = inject(I18NextService);
  private readonly toastService = inject(ToastService);

  public readonly divisionId = input<string | undefined>(undefined);

  protected isLoading = signal(false);
  protected readonly codeExists = signal(false);

  protected readonly form: FormGroup<CreateCatalogCategoryFormType> = this.fb.group({
    name: this.fb.control('', [Validators.required, Validators.maxLength(200)]),
    code: this.fb.control('', [Validators.required, Validators.maxLength(2)]),
  });

  constructor() {
    this.form.controls.code.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.codeExists.set(false));
  }

  protected closeModal() {
    this.modalCtrl.dismiss();
  }

  protected async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value: ICreateCatalogCategoryModel = {
      ...this.form.getRawValue(),
      isoLanguageCode: this.i18n.selectedLanguage,
      divisionId: this.divisionId() ?? null,
    };

    this.isLoading.set(true);
    try {
      await firstValueFrom(this.catalogClient.createCatalogCategory(new CreateCatalogCategoryModel(value)));

      this.modalCtrl.dismiss(true, 'created');
    } catch (error) {
      if (error instanceof Object && 'status' in error && error.status === 409) {
        this.codeExists.set(true);
      } else {
        await this.toastService.showError(this.i18next.t('MasterData.NewCatalogCategoryModal.CreateError'));
      }
    } finally {
      this.isLoading.set(false);
    }
  }
}
